import * as React from 'react';
import { useForm, type FieldError, type FieldPathValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DOCUMENT_METADATA_DEFAULTS,
  documentMetadataSchema,
  type DocumentMetadataFormValues,
} from '@/features/documents/schemas/documentMetadata.schema';

export type MetadataFormData = DocumentMetadataFormValues;
export type MetadataPermissions = MetadataFormData['permissions'];

export const useDocumentMetadataForm = (onSubmit: (metadata: MetadataFormData) => void) => {
  const form = useForm<MetadataFormData>({
    resolver: zodResolver(documentMetadataSchema),
    defaultValues: DOCUMENT_METADATA_DEFAULTS,
  });

  // Must read isDirty at render scope: `formState` is a Proxy that only tracks
  // flags read during render — first read inside a callback freezes it.
  const formData = form.watch();
  const { errors: fieldErrors, isDirty } = form.formState;

  const errors = React.useMemo(() => {
    const messages: Record<string, string> = {};
    // typeof guard keeps the nested `permissions` FieldErrors out of the flat record.
    Object.entries(fieldErrors).forEach(([name, error]) => {
      const message = (error as FieldError | undefined)?.message;
      if (typeof message === 'string') messages[name] = message;
    });
    return messages;
  }, [fieldErrors]);

  // Cast: TS can't reduce FieldPathValue<T, K> while K is generic; call sites
  // are still checked against the generic signature.
  const update = React.useCallback(
    <K extends keyof MetadataFormData>(key: K, value: MetadataFormData[K]) => {
      // shouldValidate deliberately off: errors recompute only on submit, as
      // pre-migration (pinned by the characterization test).
      form.setValue(key, value as FieldPathValue<MetadataFormData, K>, { shouldDirty: true });
    },
    [form],
  );

  const updatePermission = React.useCallback(
    <K extends keyof MetadataPermissions>(key: K, value: MetadataPermissions[K]) => {
      form.setValue(
        'permissions',
        { ...form.getValues('permissions'), [key]: value },
        { shouldDirty: true },
      );
    },
    [form],
  );

  /** Reports whether the tag was taken, so the caller only clears its field on success. */
  const addTag = React.useCallback(
    (raw: string): boolean => {
      const tag = raw.trim();
      const tags = form.getValues('tags');
      if (!tag || tags.includes(tag)) return false;
      form.setValue('tags', [...tags, tag], { shouldDirty: true });
      return true;
    },
    [form],
  );

  return {
    formData,
    errors,
    isDirty,
    update,
    updatePermission,
    addTag,
    // Arrow is load-bearing: keeps onSubmit single-argument (RHF also passes
    // the event; suites assert toHaveBeenCalledWith(<one object>)).
    handleSubmit: form.handleSubmit((values) => onSubmit(values)),
  };
};
